import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import { RootState } from '../../app/store';
import { PendingOperationEntry } from './types/patchTypes';
import { getOperationConflictKey } from './utils/operationIdentity';

type Operation = {
  id?: string;
  __typename: string;
  [key: string]: any;
}

type UpdateOperation = {
  id: string;
  operation: Operation;
}

type OperationTypesByPatchType = {
  _SetBundPrice: string[];
  _RmvBund: string[];
  _RmvProdVar: string[];
  _SetProdPrices: string[];
  [key: string]: string[];
}

type PendingChange = {
  id: string;
  type: string;
  data: {
    setOperations: PendingOperationEntry[];
    removeOperations: PendingOperationEntry[];
  };
}

type PatchesState = {
  patches: any[];
  patchObject: any | null;
  patchType: string | null;
  operationTypes: OperationTypesByPatchType;

  /**
   * Bumped whenever patches change on the server, so views holding their own copy know to drop it.
   *
   * A revision counter rather than a boolean flag: consumers compare it against what they last loaded
   * at, so nobody has to own resetting it, and two invalidations in a row cannot collapse into one.
   */
  patchDataRevision: number;

  // New state for patch management enhancements
  hasFetchedPatches: boolean;
  hasUnsavedChanges: boolean;
  pendingChanges: PendingChange;
}

const initialState: PatchesState = {
  patches: [],
  patchObject: null,
  patchType: null,
  patchDataRevision: 0,
  operationTypes: {
    _SetBundPrice: [
      'SetBundlePriceMenuPatchOperation'
    ],
    _RmvBund: [
      'RemoveBundleMenuPatchOperation'
    ],
    _RmvProdVar: [
      'RemoveProductMenuPatchOperation',
      'RemoveVariantMenuPatchOperation',
      'RemoveModifierFromSlotPatchOperation',
      'RemoveModifierMenuPatchOperation',
      'SetModifierWeightIsExcludedFromVariantMenuPatchOperation',
    ],
    _SetProdPrices: [
      'SetVariantPriceMenuPatchOperation',
      'SetModifierPriceRangesMenuPatchOperation',
      'SetModifierWeightPriceMenuPatchOperation'
    ]
  },
  
  // Initialize new state variables
  hasFetchedPatches: false,
  hasUnsavedChanges: false,
  pendingChanges: {
    id: '',
    type: '',
    data: {
      setOperations: [],
      removeOperations: []
    }
  }
};

/**
 * Fills in the fields a pending entry needs but a caller may not have supplied. `opId` and
 * `conflictKey` are both derivable, so they are normalised here rather than being every caller's
 * responsibility. `scope` is not derivable - an entry without one reaches no patches, and says so.
 */
const normalizeEntry = (entry: any): PendingOperationEntry => {
  const minimal = entry?.minimal ?? entry;

  if (!entry?.scope) {
    console.warn('patchesSlice: pending operation added without a scope; it will reach no patches', entry);
  }

  return {
    ...entry,
    opId: entry?.opId || uuid(),
    conflictKey: entry?.conflictKey || getOperationConflictKey(minimal),
    scope: entry?.scope ?? { kind: 'all', patchIds: [] }
  };
};

export const patchesSlice = createSlice({
  name: 'patches',
  initialState,
  reducers: {
    setPatches: (state, action: PayloadAction<any[]>) => {
      state.patches = action.payload;
    },
    /**
     * Announces that patches changed on the server, so anything caching a patch payload should refetch.
     *
     * Dispatched when a submission completes. Without it, a loaded patch keeps showing pre-submit
     * values - and since the operations editor reads its "current" and "Was $X" baseline out of that
     * cache, edits would be compared against amounts that no longer exist.
     */
    patchDataInvalidated: (state) => {
      state.patchDataRevision += 1;
    },
    setPatchObject: (state, action: PayloadAction<any>) => {
      state.patchObject = action.payload;
    },
    setPatchType: (state, action: PayloadAction<string>) => {
      state.patchType = action.payload;
    },
    addPatchOperations: (state, action: PayloadAction<{
      setOperations?: Operation[];
      removeOperations?: Operation[];
      updateOperations?: UpdateOperation[];
    }>) => {
      const { setOperations, removeOperations, updateOperations } = action.payload;
      
      if (setOperations && state.patchObject) {
        if (!state.patchObject.operations) {
          state.patchObject.operations = [];
        }
        state.patchObject.operations = [...state.patchObject.operations, ...setOperations];
      }
      
      if (removeOperations && state.patchObject) {
        if (!state.patchObject.operations) {
          state.patchObject.operations = [];
        }
        state.patchObject.operations = [...state.patchObject.operations, ...removeOperations];
      }
      
      if (updateOperations && state.patchObject && state.patchObject.operations) {
        updateOperations.forEach(update => {
          const index = state.patchObject.operations.findIndex(
            (op: Operation) => op.id === update.id
          );
          if (index !== -1) {
            state.patchObject.operations[index] = {
              ...state.patchObject.operations[index],
              ...update.operation,
              id: update.id
            };
          }
        });
      }
    },
    
    // New reducers for patch management enhancements
    setHasFetchedPatches: (state, action: PayloadAction<boolean>) => {
      state.hasFetchedPatches = action.payload;
    },
    
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    
    addPendingChange: (state, action: PayloadAction<{
        type: string;
        data: {
          setOperations?: PendingOperationEntry[];
          removeOperations?: PendingOperationEntry[];
        };
    }>) => {
      const { type, data } = action.payload;

      const setOperations = (data.setOperations || []).map(normalizeEntry);
      const removeOperations = (data.removeOperations || []).map(normalizeEntry);

      // If there's an existing pending change, merge the new data
      if (state.pendingChanges.id) {
        state.pendingChanges = {
          ...state.pendingChanges,
          type: 'Multiple Operations',
          data: {
            setOperations: [...state.pendingChanges.data.setOperations, ...setOperations],
            removeOperations: [...state.pendingChanges.data.removeOperations, ...removeOperations]
          }
        };
      } else {
        // If no existing pending change, create a new one
        state.pendingChanges = {
          id: `change-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
          type,
          data: { setOperations, removeOperations }
        };
      }

      // Always set hasUnsavedChanges to true when any operations are present
      const hasOperations = state.pendingChanges.data.setOperations.length > 0 || state.pendingChanges.data.removeOperations.length > 0;
      state.hasUnsavedChanges = hasOperations;
    },

    /**
     * Removes a pending operation by its `opId`. Indices are not a safe handle: store-level and
     * all-store entries interleave in these arrays, so an index means something different after any
     * other removal.
     */
    removeOperationFromPending: (state, action: PayloadAction<{ opId: string }>) => {
      const { opId } = action.payload;

      state.pendingChanges.data.setOperations = state.pendingChanges.data.setOperations.filter(
        (entry) => entry.opId !== opId
      );
      state.pendingChanges.data.removeOperations = state.pendingChanges.data.removeOperations.filter(
        (entry) => entry.opId !== opId
      );

      // Update hasUnsavedChanges based on remaining operations
      const hasOperations = state.pendingChanges.data.setOperations.length > 0 || state.pendingChanges.data.removeOperations.length > 0;

      // If no operations left, clear the pending change
      if (!hasOperations) {
        state.pendingChanges = initialState.pendingChanges;
        state.hasUnsavedChanges = false;
      } else {
        state.hasUnsavedChanges = true;
      }
    },

    /**
     * Revises an existing pending operation in place. Retyping a price in the operations dialog must
     * replace that edit rather than stack a second one against the same target.
     */
    updatePendingOperation: (state, action: PayloadAction<{
      opId: string;
      minimal: any;
      enriched: any;
      previousValue?: number;
    }>) => {
      const { opId, minimal, enriched, previousValue } = action.payload;

      [state.pendingChanges.data.setOperations, state.pendingChanges.data.removeOperations].forEach((entries) => {
        const index = entries.findIndex((entry) => entry.opId === opId);
        if (index === -1) return;

        entries[index] = {
          ...entries[index],
          minimal,
          enriched,
          conflictKey: getOperationConflictKey(minimal),
          // Keep the value first recorded for this target - it is the true "before", and re-editing
          // must not overwrite it with the previous edit's value.
          previousValue: entries[index].previousValue ?? previousValue
        };
      });
    },

    clearPendingChanges: (state) => {
      state.pendingChanges = initialState.pendingChanges;
      state.hasUnsavedChanges = false;
    }
  },
});

export const {
  setPatches,
  patchDataInvalidated,
  setPatchObject,
  setPatchType,
  addPatchOperations,

  // New actions for patch management enhancements
  setHasFetchedPatches,
  setHasUnsavedChanges,
  addPendingChange,
  removeOperationFromPending,
  updatePendingOperation,
  clearPendingChanges
} = patchesSlice.actions;

// Selectors
export const selectPatches = (state: RootState) => state.patches.patches;
export const selectPatchDataRevision = (state: RootState) => state.patches.patchDataRevision;

// New selectors for patch management enhancements
export const selectHasFetchedPatches = (state: RootState) => state.patches.hasFetchedPatches;
export const selectHasUnsavedChanges = (state: RootState) => state.patches.hasUnsavedChanges;
export const selectPendingChanges = (state: RootState) => state.patches.pendingChanges;

/** A pending entry together with which bucket it sits in - an edit or a deletion. */
export interface ScopedPendingOperation {
  entry: PendingOperationEntry;
  bucket: 'set' | 'remove';
}

/**
 * Direct, store-level edits grouped by the patch they were made against, so the operations dialog and
 * the named price rows can show what is already pending without rescanning both arrays. Memoised -
 * it builds a new object, which would otherwise re-render every subscriber on any state change.
 *
 * All-store entries are excluded: they belong to no single patch's edit state.
 */
export const selectStorePendingOperationsByPatch = createSelector(
  selectPendingChanges,
  (pendingChanges): Record<string, ScopedPendingOperation[]> => {
    const byPatch: Record<string, ScopedPendingOperation[]> = {};

    const collect = (entries: PendingOperationEntry[], bucket: 'set' | 'remove') => {
      entries.forEach((entry) => {
        if (entry?.scope?.kind !== 'store') return;
        const { patchId } = entry.scope;
        (byPatch[patchId] ||= []).push({ entry, bucket });
      });
    };

    collect(pendingChanges.data.setOperations, 'set');
    collect(pendingChanges.data.removeOperations, 'remove');

    return byPatch;
  }
);

export default patchesSlice.reducer;
